export interface JobFlyerPaletteOption {
  id: string;
  optionCode: string;
  label: string;
  description: string;
  bestFor: string;
}

export const DEFAULT_FLYER_OPTION_ID = 'midnight-blue';

/** Tech Opportunity flyer — color themes only (options A–I). */
export const FLYER_PALETTE_OPTIONS: JobFlyerPaletteOption[] = [
  {
    id: 'midnight-blue',
    optionCode: 'A',
    label: 'Electric Cyan',
    description: 'Dark navy TECH OPPORTUNITY flyer with electric cyan accents',
    bestFor: 'Engineering roles, developer communities, LinkedIn',
  },
  {
    id: 'royal-blue',
    optionCode: 'B',
    label: 'Royal Lime',
    description: 'Royal blue flyer with high-energy lime accents',
    bestFor: 'Enterprise IT, corporate tech hiring',
  },
  {
    id: 'azure',
    optionCode: 'C',
    label: 'Teal Mint',
    description: 'Deep teal flyer with mint technology accents',
    bestFor: 'Cloud and platform engineering posts',
  },
  {
    id: 'cobalt',
    optionCode: 'D',
    label: 'Violet Rose',
    description: 'Violet flyer with rose-pink accent highlights',
    bestFor: 'Startups and product engineering teams',
  },
  {
    id: 'deep-navy',
    optionCode: 'E',
    label: 'Slate Amber',
    description: 'Dark slate flyer with warm amber technology accents',
    bestFor: 'Default professional IT recruiter shares',
  },
  {
    id: 'light-sky',
    optionCode: 'F',
    label: 'Light Sky',
    description: 'Clean white flyer with bright sky-blue technology accents',
    bestFor: 'Bright recruiter shares and broad corporate audiences',
  },
  {
    id: 'light-mint',
    optionCode: 'G',
    label: 'Light Mint',
    description: 'Soft mint flyer with deep teal accents',
    bestFor: 'Cloud, data, and product teams',
  },
  {
    id: 'light-lavender',
    optionCode: 'H',
    label: 'Light Lavender',
    description: 'Light lavender flyer with violet accents',
    bestFor: 'Modern startups and product engineering roles',
  },
  {
    id: 'light-sunrise',
    optionCode: 'I',
    label: 'Light Sunrise',
    description: 'Warm light flyer with amber-orange accents',
    bestFor: 'Friendly hiring posts and community shares',
  },
  {
    id: 'aubergine-ember',
    optionCode: 'J',
    label: 'Aubergine Ember',
    description: 'Deep aubergine with coral, peach, and warm-cream accents',
    bestFor: 'AI, startup, and product-engineering campaigns',
  },
  {
    id: 'forest-sage',
    optionCode: 'K',
    label: 'Forest Sage',
    description: 'Warm ivory with forest, sage, mint, and muted-gold accents',
    bestFor: 'Cloud, data, sustainability, and corporate roles',
  },
  {
    id: 'crimson-print',
    optionCode: 'L',
    label: 'Crimson Print',
    description: 'Charcoal and warm ivory with crimson and dusty-rose accents',
    bestFor: 'Bold editorial campaigns and hard-to-fill roles',
  },
];

/** @deprecated Use JobFlyerPaletteOption. */
export type JobFlyerLayoutOption = JobFlyerPaletteOption;
/** @deprecated Use FLYER_PALETTE_OPTIONS. */
export const FLYER_LAYOUT_OPTIONS = FLYER_PALETTE_OPTIONS;

/** Maps deprecated theme ids stored on older flyers to current palette ids. */
export const LEGACY_FLYER_OPTION_IDS: Record<string, string> = {
  'soft-brand': 'midnight-blue',
  'bold-linkedin': 'midnight-blue',
  'royal-hiring': 'royal-blue',
  'azure-career': 'azure',
  'cobalt-community': 'cobalt',
  'emerald-growth': 'midnight-blue',
  'coral-energy': 'midnight-blue',
  'amber-classic': 'midnight-blue',
  'dark-social': 'midnight-blue',
  'typography-minimal': 'midnight-blue',
};

const optionById = (optionId?: string): JobFlyerPaletteOption | undefined =>
  FLYER_PALETTE_OPTIONS.find((option) => option.id === optionId);

export const resolveFlyerOptionId = (rawId?: string): string => {
  if (!rawId?.trim()) {
    return DEFAULT_FLYER_OPTION_ID;
  }
  const normalized = rawId.trim().toLowerCase();
  const legacy = LEGACY_FLYER_OPTION_IDS[normalized];
  if (legacy) {
    return legacy;
  }
  if (optionById(normalized)) {
    return normalized;
  }
  return DEFAULT_FLYER_OPTION_ID;
};

export const flyerOptionLabel = (optionId?: string): string => {
  const resolvedId = resolveFlyerOptionId(optionId);
  const match = optionById(resolvedId) ?? FLYER_PALETTE_OPTIONS[0];
  return `Option ${match.optionCode} · ${match.label}`;
};

export const mergeFlyerOptions = (
  remote: Partial<JobFlyerLayoutOption>[]
): JobFlyerPaletteOption[] => {
  const remoteById = new Map(
    (remote ?? []).filter((item) => item.id).map((item) => [item.id!, item])
  );

  const merged = FLYER_PALETTE_OPTIONS.map((fallback) => {
    const item = remoteById.get(fallback.id);
    if (!item) {
      return fallback;
    }
    return {
      id: fallback.id,
      optionCode: item.optionCode ?? fallback.optionCode,
      label: item.label ?? fallback.label,
      description: item.description ?? fallback.description,
      bestFor: item.bestFor ?? fallback.bestFor,
    };
  });

  for (const item of remote ?? []) {
    if (!item.id || merged.some((option) => option.id === item.id)) {
      continue;
    }
    merged.push({
      id: item.id,
      optionCode: item.optionCode ?? '?',
      label: item.label ?? item.id,
      description: item.description ?? '',
      bestFor: item.bestFor ?? '',
    });
  }

  return merged.sort((left, right) =>
    left.optionCode.localeCompare(right.optionCode)
  );
};

export const isLightFlyerOption = (option: JobFlyerPaletteOption): boolean =>
  [
    'light-sky',
    'light-mint',
    'light-lavender',
    'light-sunrise',
    'forest-sage',
  ].includes(option.id);
